//src/Components/Buttons/MultiFileUploader/useAttachments.tsx
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { IDocument } from 'app/services/documents';
import UploadDocumentButton from 'Components/Buttons/UploadDocumentButton';
import { uniqueId } from 'lodash';
import { useEffect, useState } from 'react';

export interface IUploaderState {
  attachments: IAttachment[];
  isAllUploadedOrEmpty: boolean;
  isAllValidOrEmpty: boolean;
  state: 'staging' | 'uploading' | 'finished' | 'reset';
}

export interface AttachmentErrors {
  maxFileSizeExceeded?: string;
  invalidFileType?: string;
  maxFilesCount?: string;
}
export interface IAttachment {
  staged: File;
  uploaded?: IDocument;
  queryStatus: QueryStatus;
  id: string;
  errors?: AttachmentErrors;
}

export const initialUploaderState: IUploaderState = {
  attachments: [],
  isAllUploadedOrEmpty: true,
  isAllValidOrEmpty: true,
  state: 'staging',
};
interface UploaderProps {
  maxFilesCount?: number;
  maxFileSize?: number;
  onNewAttachment?: (attachment: IAttachment) => void;
}

const maxFileSizeExceededError = (maxFileSize: number) => ({
  maxFileSizeExceeded: `File is too large: Upload up to ${Math.floor(
    maxFileSize / 1024 / 1024,
  )} MB`,
});

const maxFilesCountExceededError = (maxFilesCount: number) => ({
  maxFilesCount: `Too many files selected: Attach up to ${maxFilesCount} files`,
});

export default function useAttachments({
  maxFileSize,
  maxFilesCount,
  onNewAttachment = () => {},
}: UploaderProps) {
  const [isValid, setIsValid] = useState();
  const [errors, setErrors] = useState<AttachmentErrors>();
  const [attachments, setAttachements] = useState<IAttachment[]>();
  const initAttachements = (files: File[]) =>
    files.map((file) => {
      const attachment: IAttachment = {
        staged: file,
        errors:
          maxFileSize !== undefined && file.size > maxFileSize
            ? maxFileSizeExceededError(maxFileSize)
            : undefined,
        id: uniqueId(),
        queryStatus: QueryStatus.uninitialized,
      };
      onNewAttachment(attachment);
      return attachment;
    });

  const addAttachements = (files: File[]) => {
    setAttachements((attachments || []).concat(initAttachements(files)));
  };

  const deleteAttachment = (attachment: IAttachment) => {
    setAttachements((attachments || []).filter((a) => a.id !== attachment.id));
  };

  const deleteAllAttachments = () => {
    setAttachements(undefined);
  };

  useEffect(() => {
    if (!attachments) {
      setErrors(() => undefined);
    } else {
      let errors: AttachmentErrors = {};

      if (attachments.some((a) => !!a.errors?.invalidFileType)) {
        errors = {
          invalidFileType: 'Invalid file type',
        };
      }

      if (
        maxFileSize !== undefined &&
        attachments.some((a) => !!a.errors?.maxFileSizeExceeded)
      ) {
        errors = {
          ...errors,
          ...maxFileSizeExceededError(maxFileSize),
        };
      }

      if (maxFilesCount !== undefined && attachments.length > maxFilesCount) {
        errors = {
          ...errors,
          ...maxFilesCountExceededError(maxFilesCount),
        };
      }

      setErrors(() => (Object.values(errors).length > 0 ? errors : undefined));
    }
  }, [attachments]);

  return {
    attachments,
    deleteAttachment,
    addAttachements,
    deleteAllAttachments,
    errors,
  };
}
